if (!customElements.get('annur-mix-builder')) {
  customElements.define(
    'annur-mix-builder',
    class AnnurMixBuilder extends HTMLElement {
      connectedCallback() {
        this.checkboxes = [...this.querySelectorAll('[data-mix-checkbox]')];
        this.status = this.querySelector('[data-mix-status]');
        this.error = this.querySelector('[data-mix-error]');
        this.submitButton = this.querySelector('[data-mix-submit]');
        this.minimumItems = Number(this.dataset.minimumItems) || 2;
        this.maximumItems = Math.max(this.minimumItems, Number(this.dataset.maximumItems) || 4);
        this.discountPercentage = Number(this.dataset.discountPercentage) || 0;
        this.mixContract = this.dataset.mixContract || '';
        this.variants = new Map(
          JSON.parse(this.querySelector('[data-main-variants]')?.textContent || '[]').map((variant) => [
            String(variant.id),
            variant,
          ]),
        );
        this.mainVariantId = String(this.dataset.mainVariantId);
        this.viewPublished = false;

        this.addEventListener('change', this.onChange);
        this.submitButton?.addEventListener('click', this.onSubmit);
        this.observeView();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data?.variant) return;
            this.mainVariantId = String(data.variant.id);
            this.update();
          });
        }

        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.submitButton?.removeEventListener('click', this.onSubmit);
        this.viewObserver?.disconnect();
        this.unsubscribeVariant?.();
      }

      onChange = (event) => {
        const checkbox = event.target.closest('[data-mix-checkbox]');
        const exceedsMaximum = checkbox && checkbox.checked && this.selectedOptions.length + 1 > this.maximumItems;
        if (exceedsMaximum) {
          checkbox.checked = false;
          this.showError(`Puedes combinar un máximo de ${this.maximumItems} libros.`);
        } else {
          this.showError();
        }

        if (checkbox) {
          const option = checkbox.closest('[data-mix-option]');
          const selector = option?.querySelector('select[data-mix-variant]');
          if (selector) selector.disabled = !checkbox.checked;
          if (!exceedsMaximum) this.publishSelection(option, checkbox.checked);
        }

        this.update();
      };

      onSubmit = async (event) => {
        const mainVariant = this.currentMainVariant;
        if (!mainVariant?.available || this.selectedOptions.length + 1 < this.minimumItems) return;
        const selectedOptions = this.selectedOptions;
        const submittedSubtotal =
          Number(mainVariant.price) +
          selectedOptions.reduce((total, option) => total + (Number(this.variantFor(option)?.dataset.price) || 0), 0);

        const mixId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
        const mixLabel = `Mezcla ${mixId.slice(-4).toUpperCase()}`;

        event.currentTarget.disabled = true;
        event.currentTarget.setAttribute('aria-busy', 'true');
        this.showError();

        const items = [
          this.buildItem(mainVariant.id, mixId, mixLabel, 'main'),
          ...selectedOptions.map((option) =>
            this.buildItem(this.variantFor(option).value, mixId, mixLabel, 'recommended'),
          ),
        ];

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message);

          const cart = await this.fetchCart();
          const appliedDiscount = this.discountForGroup(cart, mixId);

          this.publish('annur:upsell_added_to_cart', {
            source_product_id: String(this.dataset.productId),
            recommended_product_ids: selectedOptions.map((option) => String(option.dataset.productId)),
            recommended_variant_ids: selectedOptions.map((option) => String(this.variantFor(option).value)),
            bundle_size: items.length,
            value: submittedSubtotal / 100,
            currency: this.dataset.currency,
            placement: 'product_page',
            source_mode: this.dataset.sourceMode,
            discount_percentage: this.discountPercentage,
            discount_value: appliedDiscount / 100,
            discount_verified: appliedDiscount > 0,
          });
          window.location.assign(this.destinationForCart());
        } catch (error) {
          this.showError(error?.message || 'No se pudo añadir la mezcla. Inténtalo de nuevo.');
          event.currentTarget.disabled = false;
          event.currentTarget.removeAttribute('aria-busy');
        }
      };

      observeView() {
        if (!('IntersectionObserver' in window)) return;
        this.viewObserver = new IntersectionObserver(
          (entries) => {
            const isVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
            if (this.viewPublished || !isVisible) return;

            this.viewPublished = true;
            this.publish('annur:upsell_viewed', {
              source_product_id: String(this.dataset.productId),
              placement: 'product_page',
              source_mode: this.dataset.sourceMode,
              recommendation_count: Number(this.dataset.recommendationCount) || 0,
            });
            this.viewObserver.disconnect();
          },
          { threshold: 0.5 },
        );
        this.viewObserver.observe(this.querySelector('.annur-product-mix__header') || this);
      }

      publishSelection(option, selected) {
        const variant = this.variantFor(option);
        if (!option || !variant) return;
        this.publish('annur:upsell_product_selected', {
          source_product_id: String(this.dataset.productId),
          recommended_product_id: String(option.dataset.productId),
          recommended_variant_id: String(variant.value),
          selected,
          quantity: 1,
          value: (Number(variant.dataset.price) || 0) / 100,
          currency: this.dataset.currency,
          placement: 'product_page',
          source_mode: this.dataset.sourceMode,
        });
      }

      publish(eventName, payload) {
        window.AnnurAnalytics?.publish(eventName, payload);
      }

      buildItem(variantId, mixId, mixLabel, role) {
        return {
          id: Number(variantId),
          quantity: 1,
          properties: {
            'Comprados juntos': mixLabel,
            _Annur_mix_id: mixId,
            _Annur_mix_origin: this.dataset.bundleKey,
            _Annur_mix_role: role,
            _Annur_mix_contract: this.mixContract,
          },
        };
      }

      update() {
        const totalItems = this.selectedOptions.length + 1;
        const subtotal = this.subtotal;
        const ready = totalItems >= this.minimumItems && totalItems <= this.maximumItems;
        const savings = ready ? this.estimateDiscount(subtotal) : 0;

        if (this.status) {
          this.status.textContent = ready
            ? `${totalItems} libros seleccionados. La mezcla está lista.`
            : `Selecciona ${this.minimumItems - totalItems} libro más para completar la mezcla.`;
        }
        if (this.submitButton) this.submitButton.disabled = !ready || !this.currentMainVariant?.available;

        const offer = this.querySelector('[data-mix-offer]');
        const offerTitle = this.querySelector('[data-mix-offer-title]');
        offer?.classList.toggle('is-pending', !ready);
        if (offerTitle) {
          offerTitle.textContent = ready
            ? `${this.discountPercentage} % de descuento activado para esta selección`
            : 'Selecciona al menos un libro recomendado';
        }

        const subtotalElement = this.querySelector('[data-mix-subtotal]');
        const savingsElement = this.querySelector('[data-mix-savings]');
        const totalElement = this.querySelector('[data-mix-total]');
        const mainPriceElement = this.querySelector('[data-main-price]');
        if (subtotalElement) subtotalElement.textContent = this.formatMoney(subtotal);
        if (savingsElement) savingsElement.textContent = `−${this.formatMoney(savings)}`;
        if (totalElement) totalElement.textContent = this.formatMoney(subtotal - savings);
        if (mainPriceElement) mainPriceElement.textContent = this.formatMoney(this.currentMainVariant?.price || 0);

        for (const option of this.querySelectorAll('[data-mix-option]')) {
          const price = option.querySelector('.annur-mix-option__price');
          const variant = this.variantFor(option);
          if (price && variant) price.textContent = this.formatMoney(Number(variant.dataset.price) || 0);
        }
      }

      variantFor(option) {
        const selector = option?.querySelector('[data-mix-variant]');
        if (!selector) return null;
        if (selector.tagName === 'SELECT') return selector.selectedOptions[0];
        return selector;
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
          style: 'currency',
          currency: this.dataset.currency || 'EUR',
        }).format(cents / 100);
      }

      estimateDiscount(cents) {
        return Math.round((cents * this.discountPercentage) / 100);
      }

      async fetchCart() {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!response.ok) return null;
        return response.json();
      }

      discountForGroup(cart, mixId) {
        if (!cart?.items) return 0;
        return cart.items
          .filter(
            (item) =>
              item.properties?._Annur_mix_id === mixId && item.properties?._Annur_mix_contract === this.mixContract,
          )
          .flatMap((item) => item.line_level_discount_allocations || [])
          .filter((allocation) => {
            const title = allocation.discount_application?.title || '';
            return title === this.dataset.discountTitle || title.includes('Comprados juntos');
          })
          .reduce((total, allocation) => total + (Number(allocation.amount) || 0), 0);
      }

      showError(message = '') {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = !message;
      }

      get selectedOptions() {
        return this.checkboxes
          .filter((checkbox) => checkbox.checked)
          .map((checkbox) => checkbox.closest('[data-mix-option]'));
      }

      get currentMainVariant() {
        const formVariant = document.querySelector(
          `product-info[data-product-id="${this.dataset.productId}"] product-form .product-variant-id`,
        );
        const variantId = String(formVariant?.value || this.mainVariantId);
        return this.variants.get(variantId);
      }

      get subtotal() {
        const relatedTotal = this.selectedOptions.reduce((total, option) => {
          return total + (Number(this.variantFor(option)?.dataset.price) || 0);
        }, 0);
        return (Number(this.currentMainVariant?.price) || 0) + relatedTotal;
      }

      destinationForCart() {
        return window.routes.cart_url;
      }
    },
  );
}
